import { useId, useRef, useState, useEffect } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClipPlayback } from '../../../../hooks/useClipPlayback';
import * as prospectMediaService from '../../../../services/prospect-media';
import { getValidatedGcsUrl } from '../../../../utils/url-validation';
import { formatVideoDate, formatVideoDuration } from '../../../../utils/video-format';
import { isClip as isClipVideo } from '../../../../types/prospect-media';
import type { VideoTag, VideoWithProspect } from '../../../../types/prospect-media';
import { Button } from '../../../../components/Button';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import { VideoDownloadButton } from '../../../../components/VideoDownloadButton';
import { VideoTagEditor } from './VideoTagEditor';
import './VideoViewerPanel.css';

interface VideoViewerPanelProps {
  video: VideoWithProspect;
  isOpen: boolean;
  onClose: () => void;
  /** Lets the grid behind the panel reflect a tag change without a refetch. */
  onTagsChange?: (videoId: string, tags: VideoTag[]) => void;
  /** Called after the video is deleted, so the caller can drop it from the grid and close. */
  onDeleted?: (videoId: string) => void;
  /** Opens the clip editor for this video. Not offered on a clip: a clip can't be clipped again. */
  onClip?: (video: VideoWithProspect) => void;
  /** A tag was deleted for the whole staff; other videos on screen still show it until told. */
  onTagDeleted?: (tagId: string) => void;
}

export const VideoViewerPanel = ({
  video,
  isOpen,
  onClose,
  onTagsChange,
  onDeleted,
  onClip,
  onTagDeleted,
}: VideoViewerPanelProps) => {
  const navigate = useNavigate();
  const headingId = useId();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Whether the in-flight click actually started on the backdrop, so releasing a drag that
  // began inside the player (scrubbing, selecting text) does not dismiss it.
  const backdropMouseDown = useRef(false);

  // Clips share their source's file; confine playback to the clip's window.
  const { videoRef, isClip, handlers } = useClipPlayback(
    video.clipStartSeconds,
    video.clipEndSeconds
  );

  const prospectName = `${video.prospectFirstName} ${video.prospectLastName}`;
  const duration = formatVideoDuration(video.durationSeconds);

  // Title, date and uploader on one line. Built from what is actually set — an upload with
  // no title and no recorded uploader would otherwise render as " · Aug 12, 2026 · ".
  const metaParts = [
    video.title,
    formatVideoDate(video.createdAt),
    video.uploadedByName ? `by ${video.uploadedByName}` : null,
  ].filter(Boolean);

  useEffect(() => {
    if (!isOpen) return;

    // Validate the GCS URL
    const validatedUrl = getValidatedGcsUrl(video.gcsUrl);
    if (!validatedUrl) {
      setError('Invalid video URL');
      console.error('[VideoViewerPanel] Invalid or non-GCS URL', {
        videoId: video.id,
        receivedUrl: video.gcsUrl,
      });
    } else {
      setVideoUrl(validatedUrl);
      setError(null);
    }
  }, [video.gcsUrl, video.id, isOpen]);

  // A failed delete leaves its message on screen; opening a different video must not
  // inherit it.
  useEffect(() => {
    setDeleteError(null);
    setConfirmingDelete(false);
  }, [video.id]);

  // ESC key handler. While the delete confirm is open it owns Escape — ConfirmModal stops
  // the event in the capture phase, so this listener never sees it and the panel stays put.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Backdrop dismissal, matching ConfirmModal: the click must both start and end on the
  // overlay itself. Testing the target beats a stopPropagation handler on the container —
  // scrubbing the seek bar and releasing outside the dialog no longer closes the player,
  // and the container stays a plain non-interactive element.
  const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    backdropMouseDown.current = e.target === e.currentTarget;
  };

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (!backdropMouseDown.current) return;
    backdropMouseDown.current = false;
    onClose();
  };

  const handleViewProspect = () => {
    // Mirrors the PlayersSection deep link used by the call list. Land on Videos — that's
    // the tab the coach was already looking at, so the context carries over.
    const params = new URLSearchParams({
      prospectId: video.prospectId,
      section: 'videos',
    });
    onClose();
    navigate(`/players?${params.toString()}`);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    setDeleteError(null);

    try {
      await prospectMediaService.deleteVideo(video.prospectId, video.id);
    } catch (err) {
      console.error('[VideoViewerPanel] Failed to delete video', {
        error: err,
        prospectId: video.prospectId,
        videoId: video.id,
      });
      setDeleting(false);
      setConfirmingDelete(false);
      setDeleteError('Could not delete this video. Please try again.');
      return;
    }

    setDeleting(false);
    setConfirmingDelete(false);
    // The caller drops the tile and closes the panel; this component does neither itself,
    // so the grid and the panel can never disagree about whether the video still exists.
    onDeleted?.(video.id);
  };

  return (
    <>
      <div
        className="video-viewer-panel__overlay"
        onMouseDown={handleBackdropMouseDown}
        onClick={handleBackdropClick}
      >
        <div
          className="video-viewer-panel__container"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
        >
          <div className="video-viewer-panel__stage">
            {error && <div className="video-viewer-panel__error">{error}</div>}
            {videoUrl && !error && (
              <video
                ref={videoRef}
                className="video-viewer-panel__video"
                // A clip starts at an offset into a shared file, so the browser needs metadata
                // before it can seek there.
                preload={isClip ? 'auto' : undefined}
                src={videoUrl}
                controls
                autoPlay
                onLoadedMetadata={handlers.onLoadedMetadata}
                onTimeUpdate={handlers.onTimeUpdate}
                onSeeking={handlers.onSeeking}
                onError={() => setError('Unable to play video')}
              />
            )}
            <button
              className="video-viewer-panel__close"
              onClick={onClose}
              aria-label="Close video player"
            >
              ×
            </button>
          </div>

          <div className="video-viewer-panel__details">
            <div className="video-viewer-panel__heading">
              <div className="video-viewer-panel__identity">
                <h2 className="video-viewer-panel__prospect" id={headingId}>
                  {prospectName}
                </h2>
                {metaParts.length > 0 && (
                  <p className="video-viewer-panel__meta">{metaParts.join(' · ')}</p>
                )}
              </div>
              <div className="video-viewer-panel__badges">
                {isClipVideo(video) && (
                  <span className="video-viewer-panel__badge">Clip</span>
                )}
                {duration && (
                  <span className="video-viewer-panel__badge video-viewer-panel__badge--quiet">
                    {duration}
                  </span>
                )}
              </div>
            </div>

            <VideoTagEditor
              prospectId={video.prospectId}
              videoId={video.id}
              tags={video.tags ?? []}
              onTagsChange={(tags) => onTagsChange?.(video.id, tags)}
              onTagDeleted={onTagDeleted}
            />

            <div className="video-viewer-panel__actions">
              {deleteError && (
                <p className="video-viewer-panel__action-error" role="alert">
                  {deleteError}
                </p>
              )}
              <VideoDownloadButton
                key={video.id}
                prospectId={video.prospectId}
                videoId={video.id}
                isClip={isClipVideo(video)}
              />
              {onClip && !isClipVideo(video) && (
                <Button variant="secondary" onClick={() => onClip(video)}>
                  Create Clips
                </Button>
              )}
              <Button variant="secondary" onClick={handleViewProspect}>
                View Prospect
              </Button>
              <Button
                variant="secondary"
                className="video-viewer-panel__delete"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete Video
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Outside the overlay on purpose: nested inside it, a click on this modal's own
          backdrop would bubble to the overlay's onClose and shut the player too. */}
      <ConfirmModal
        isOpen={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Video"
        message="Are you sure you want to delete this video? This action cannot be undone."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        busy={deleting}
      />
    </>
  );
};
